import { useMemo } from 'react';
import { Wifi } from 'lucide-react';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Skeleton } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useConnections } from '@/api/media/live-live-streams';
import type { ConnectionItem, LiveStreamItem } from '@/api/media/live-live-streams-types';
import styles from './ConnectionTab.module.css';

interface Props {
  serviceId: string;
  stream: LiveStreamItem;
}

/** API returns strings like "2026-06-10 04:37:26.323" — pass through as-is. */
const fmtDateStr = (v?: string) => v || '—';

export function ConnectionTab({ serviceId, stream }: Props) {
  const { data: connections = [], isLoading, isError } = useConnections(
    serviceId,
    stream.id,
  );

  const columns = useMemo<ColumnDef<ConnectionItem, unknown>[]>(
    () => [
      {
        id: 'fqdn',
        header: 'Endpoint',
        cell: ({ row }) => (
          <span className={styles.cellMuted}>{row.original.fqdn || '—'}</span>
        ),
      },
      {
        id: 'clientIp',
        header: 'Client IP',
        size: 150,
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-default)' }}>{row.original.clientIp || '—'}</span>
        ),
      },
      {
        id: 'start',
        header: 'Start Time',
        size: 210,
        cell: ({ row }) => (
          <span className={styles.cellMuted}>{fmtDateStr(row.original.start)}</span>
        ),
      },
      {
        id: 'end',
        header: 'End Time',
        size: 210,
        cell: ({ row }) => (
          <span className={styles.cellMuted}>{fmtDateStr(row.original.end)}</span>
        ),
      },
    ],
    [],
  );

  if (isLoading) {
    return (
      <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} variant="rect" height={44} />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <Alert tone="warning" title="Failed to load connections">
        Could not retrieve connection data for this stream. Try refreshing the page.
      </Alert>
    );
  }

  return (
    <DataTable
      columns={columns}
      data={connections}
      getRowId={(r) => `${r.clientIp}-${r.start}-${r.fqdn}`}
      dense
      emptyState={
        <EmptyState
          icon={<Wifi size={20} />}
          title="No connections"
          description="No ingest connections recorded for this stream."
        />
      }
    />
  );
}

export default ConnectionTab;
